// API Client with CSRF protection
// Centralized axios instance for all API requests

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getToken } from './tokenStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Store CSRF token in memory
let csrfToken: string | null = null;

/**
 * FETCH CSRF token from backend
 * Called on app initialization and when token is missing
 */
async function fetchCsrfToken(): Promise<string> {
  try {
    const response = await axios.get<{ csrfToken: string }>(`${API_URL}/api/csrf-token`, {
      withCredentials: true, // Important: send cookies to receive CSRF cookie
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
}

/**
 * CREATE configured axios instance
 * - Sends credentials (cookies) with all requests
 * - Includes CSRF token in state-changing requests
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Send cookies with all requests
});

/**
 * REQUEST interceptor
 * - Add CSRF token to state-changing requests (POST, PUT, PATCH, DELETE)
 * - Add Authorization header with auth token (from localStorage)
 * - Fetch CSRF token if not available
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const method = config.method?.toUpperCase();

    // Initialize headers if not present
    config.headers = config.headers || {};

    // ADD Authorization header if auth token exists
    const authToken = getToken();
    if (authToken) {
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Only add CSRF token for state-changing methods
    if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      // Fetch token if not available
      if (!csrfToken) {
        await fetchCsrfToken();
      }

      // Add CSRF token to headers
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * RESPONSE interceptor
 * - Handle CSRF token errors
 * - Refetch token and retry request if token is invalid
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If CSRF token is invalid and we haven't retried yet
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === 'invalid csrf token' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      // Refetch CSRF token
      await fetchCsrfToken();

      // Retry original request with new token
      if (csrfToken) {
        originalRequest.headers['x-csrf-token'] = csrfToken;
      }

      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

/**
 * INITIALIZE CSRF token on app load
 * Call this when the app starts
 */
export async function initializeCsrfToken(): Promise<void> {
  try {
    await fetchCsrfToken();
  } catch (error) {
    console.error('Failed to initialize CSRF token:', error);
  }
}

export default apiClient;
